import moment from 'moment';
import enLocale from 'moment/locale/en-gb';
import esLocale from 'moment/locale/es';
import deLocale from 'moment/locale/de';
import itLocale from 'moment/locale/it';
import frLocale from 'moment/locale/fr';
import plLocale from 'moment/locale/pl';
import ptLocale from 'moment/locale/pt';
import svLocale from 'moment/locale/sv';

import {
  ENGLISH_USA,
  ENGLISH,
  FRENCH,
  GERMAN,
  ITALIAN,
  POLISH,
  PORTUGUESE,
  SPANISH,
  SWEDISH,
} from 'config/types/language';

export const DATE_FORMAT = 'MMMM D, YYYY';
export const DATE_FORMAT_FEATURE = 'MMMM D, YYYY | h:mm A';

const momentLocaleTypeMap = {
  [ENGLISH]: enLocale,
  [ENGLISH_USA]: enLocale,
  [FRENCH]: frLocale,
  [GERMAN]: deLocale,
  [ITALIAN]: itLocale,
  [SPANISH]: esLocale,
  [POLISH]: plLocale,
  [PORTUGUESE]: ptLocale,
  [SWEDISH]: svLocale,
};

/**
 * Return the correct date format for an Article page
 *
 * @param  {string} date - Post Date
 * @param  {string} languageCode - Edition Language
 * @return {string}
 */
export function formatDate(date, languageCode) {
  const locale = momentLocaleTypeMap[languageCode];

  moment.locale(languageCode, locale);
  const formatString = (languageCode === ENGLISH || languageCode === ENGLISH_USA) ? // eslint-disable-line max-len
    'MMMM D, YYYY' : 'DD/MM/YYYY';
  const formattedDate = moment(date).format(formatString);

  return formattedDate;
}

/**
 * Return the correct date format depending on how long ago the content was published
 *
 * @param  {number} timestamp - Content timestamp
 * @param {string} format
 * @return {object}
 */
export function getMomentFormatProps(timestamp, format = DATE_FORMAT) {
  const current = moment();
  const comparison = moment(timestamp);
  const diff = current.diff(comparison, 'minutes');

  // Greater than 30 minutes difference
  if (30 < diff || comparison.isAfter(current)) {
    return { format };
  }

  return { fromNow: true };
}

/**
 * Build an object representing the local time converted to UTC
 *
 * @returns {object} An object representing the local time converted to UTC
 */
export function getCurrentDateUTC() {
  const currentDate = new Date();
  return {
    year: currentDate.getUTCFullYear(),
    month: currentDate.getUTCMonth() + 1, // Normalize because month is zero-indexed
    day: currentDate.getUTCDate(),
    hour: currentDate.getUTCHours(),
    minute: currentDate.getUTCMinutes(),
    second: currentDate.getUTCSeconds(),
    millisecond: currentDate.getUTCMilliseconds(),
  };
}

/**
 * Determine whether the target date falls on or between the provided start and end dates
 *
 * NOTE: all dates are processed as UTC, hence the UTC suffix for the function name
 *
 * Expected object format for all parameters:
 * {
 *   {integer} year,    *required*
 *   {integer} month,   *required*
 *   {integer} day,     *required*
 *   {integer} hour,  (in 24-hour time)
 *   {integer} minute,
 *   {integer} second,
 *   {integer} millisecond,
 * }
 *
 * @param {object} startDate - Start date of the range
 * @param {object} endDate - End date of the range
 * @param {object} [targetDate=Now] - The date to check against the range.
 *
 * @returns {boolean} True if the target date is in the range, otherwise false
 */
export function isDateInRangeUTC(startDate, endDate, targetDate) {
  if (!targetDate) {
    const currentDate = new Date();
    targetDate = { // eslint-disable-line no-param-reassign
      year: currentDate.getUTCFullYear(),
      month: currentDate.getUTCMonth() + 1, // Normalize because month is zero-indexed
      day: currentDate.getUTCDate(),
      hour: currentDate.getUTCHours(),
      minute: currentDate.getUTCMinutes(),
      second: currentDate.getUTCSeconds(),
      millisecond: currentDate.getUTCMilliseconds(),
    };
  }

  // Compute dates as UNIX timestamps so we can compare with relational operators (e.g. greater than)
  const startDateInMs = Date.UTC(
    startDate.year,
    startDate.month,
    startDate.day,
    startDate.hour || 0,
    startDate.minute || 0,
    startDate.second || 0,
    startDate.millisecond || 0,
  );

  const isSetEndDateTime = 'hour' in endDate && !isNaN(endDate.hour);
  const endDateInMs = Date.UTC(
    endDate.year,
    endDate.month,
    endDate.day,
    endDate.hour || 23,
    endDate.minute || (isSetEndDateTime ? 0 : 59), // Default to time at end of day unless a time value is set
    endDate.second || (isSetEndDateTime ? 0 : 59), // Default to time at end of day unless a time value is set
    endDate.millisecond || (isSetEndDateTime ? 0 : 999), // Default to time at end of day unless a time value is set
  );

  const targetDateInMs = Date.UTC(
    targetDate.year,
    targetDate.month,
    targetDate.day,
    targetDate.hour || 0,
    targetDate.minute || 0,
    targetDate.second || 0,
    targetDate.millisecond || 0,
  );

  if (targetDateInMs >= startDateInMs && targetDateInMs <= endDateInMs) {
    return true;
  }
  return false;
}
